<template>
  <div class="hris-page">
    <div class="hris-page-inner">
      <div class="hris-page-header">
        <span class="hris-page-eyebrow">Timekeeping</span>
        <h1 class="hris-page-title">Loan Deductions</h1>
        <p class="hris-page-subtitle">Manage employee loans, loan types, and bulk uploads.</p>
      </div>

      <div class="hris-toolbar">
        <q-btn unelevated class="hris-q-outline" label="Add Loan Type" @click="openDialog"></q-btn>
        <q-btn unelevated class="hris-q-outline" label="Loan Template" @click="getTemplate"></q-btn>
        <q-btn unelevated class="hris-q-outline" label="Add Manual Loan" @click="addManual"></q-btn>
        <div class="hris-toolbar-spacer"></div>
        <q-btn unelevated class="hris-q-primary" label="Upload Loan" @click="uploadLoan"></q-btn>
      </div>

      <div class="hris-toolbar">
        <q-input outlined dense color="green" v-model="fromDay" mask="date" :rules="['date']" label="From">
          <template v-slot:append>
            <i class="far fa-calendar-alt cursor-pointer">
              <q-popup-proxy ref="qDateProxy" transition-show="scale" transition-hide="scale">
                <q-date v-model="fromDay">
                  <div class="row items-center justify-end">
                    <q-btn v-close-popup label="Close" color="primary" flat></q-btn>
                  </div>
                </q-date>
              </q-popup-proxy>
            </i>
          </template>
        </q-input>

        <q-input outlined dense color="green" v-model="toDay" mask="date" :rules="['date']" label="To">
          <template v-slot:append>
            <i class="far fa-calendar-alt cursor-pointer">
              <q-popup-proxy ref="qDateProxy" transition-show="scale" transition-hide="scale">
                <q-date v-model="toDay">
                  <div class="row items-center justify-end">
                    <q-btn v-close-popup label="Close" color="primary" flat></q-btn>
                  </div>
                </q-date>
              </q-popup-proxy>
            </i>
          </template>
        </q-input>
        <q-btn unelevated class="hris-q-primary" label="Filter Data" @click="filterData"></q-btn>
      </div>

      <div class="hris-panel">
        <q-table :dense="$q.screen.lt.md" :grid="$q.screen.xs" :data="generatedData"
          :columns="columns" row-key="id" separator="cell" :pagination.sync="pagination" flat>
          <template v-slot:body="props">
            <q-tr slot="body" :props="props">
              <q-td key="empID" :props="props">{{ props.row.empID }}</q-td>
              <q-td key="name" :props="props">{{ props.row.name }}</q-td>
              <q-td key="loanType" :props="props">{{ props.row.loanType }}</q-td>
              <q-td key="loanAmount" :props="props">{{ props.row.loanAmount }}</q-td>
              <q-td key="actualAmount" :props="props">{{ props.row.actualAmount }}</q-td>
              <q-td key="loadDate" :props="props">{{ props.row.loadDate }}</q-td>
              <q-td key="startDate" :props="props">{{ props.row.startDate }}</q-td>
              <q-td key="endDate" :props="props">{{ props.row.endDate }}</q-td>
              <q-td key="monthlyDeduction" :props="props">{{ props.row.monthlyDeduction }}</q-td>
              <q-td key="loanBalance" :props="props">{{ props.row.loanBalance }}</q-td>
              <q-td key="status" :props="props">
                <span
                  class="hris-badge"
                  :class="(props.row.status == 'ACTIVE') ? 'hris-badge-accent' : (props.row.status == 'HOLD') ? 'hris-badge-danger' : 'hris-badge-success'"
                >{{ props.row.status }}</span>
              </q-td>
              <q-td key="holdOrActive" :props="props">
                <q-toggle v-model="props.row.holdOrActive" :disable="(props.row.status == 'PAID') ? true : false"
                  @input="setState(props.row.status, props.row.id)" size="lg" checked-icon="fas fa-check" color="green"
                  unchecked-icon="fas fa-times"></q-toggle>
              </q-td>
              <q-td>
                <div class="row q-gutter-sm no-wrap">
                  <q-btn outline round color="green" icon="fas fa-calendar-alt" @click="showHistory(props.row.id)">
                    <q-tooltip content-class="bg-green" anchor="top middle" self="bottom middle" :offset="[10, 10]">
                      <strong>Deduction History</strong>
                    </q-tooltip>
                  </q-btn>
                  <q-btn outline round color="orange" icon="fas fa-edit"
                    :disable="(props.row.status == 'PAID') ? true : false" @click="editLoan(props.row)">
                    <q-tooltip content-class="bg-orange" anchor="top middle" self="bottom middle" :offset="[10, 10]">
                      <strong>Edit Loan</strong>
                    </q-tooltip>
                  </q-btn>
                </div>
              </q-td>
            </q-tr>
          </template>
          <template v-slot:no-data>
            <div class="hris-empty">No loan records found.</div>
          </template>
          <template v-slot:top>
            <q-input dense outlined color="green" v-model="searchKey" placeholder="Search Employee">
              <template v-slot:append><i class="fas fa-search"></i></template>
            </q-input>
            <q-space></q-space>
            <q-btn unelevated class="hris-q-outline" label="Download Excel" no-caps style="margin-right: 10px;" @click="exportTable('xlsx')"></q-btn>
            <q-btn unelevated class="hris-q-primary" label="Download CSV" no-caps @click="exportTable('csv')"></q-btn>
          </template>
        </q-table>
      </div>

      <q-dialog v-model="spinner" persistent>
        <div class="hris-loading" style="background: transparent; min-width: 120px;">
          <q-spinner color="green" size="48px"></q-spinner>
        </div>
      </q-dialog>

      <!-- add manual loan -->
      <q-dialog v-model="manual" persistent>
        <div class="hris-modal">
          <div class="hris-modal-header">
            <h2 class="hris-modal-title">{{ loanTitle }}</h2>
          </div>
          <q-form @submit="onSubmit">
            <div class="hris-modal-body">
              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input outlined dense color="green" v-model="employee_id" label="Employee ID" lazy-rules
                    :rules="[val => val && val.length > 0 || 'This field is required']"></q-input>
                </div>
              </div>
              <div class="hris-form-row">
                <div class="hris-form-field">
                  <q-select outlined dense color="green" v-model="loan_type" :options="loanTypeOptions" label="Loan Type"></q-select>
                </div>
                <div class="hris-form-field">
                  <q-input outlined dense color="green" v-model="loan_amt" label="Loan Amount" lazy-rules
                    :rules="[val => val && val.length > 0 || 'This field is required']"></q-input>
                </div>
                <div class="hris-form-field">
                  <q-input outlined dense color="green" v-model="actual_amt" label="Actual Amount" lazy-rules
                    :rules="[val => val && val.length > 0 || 'This field is required']"></q-input>
                </div>
              </div>

              <div class="hris-form-row">
                <div class="hris-form-field">
                  <q-input outlined dense color="green" v-model="loan_date" mask="date" label="Date Loan">
                    <template v-slot:append>
                      <i class="far fa-calendar-alt cursor-pointer">
                        <q-popup-proxy ref="qDateProxy" transition-show="scale" transition-hide="scale">
                          <q-date v-model="loan_date">
                            <div class="row items-center justify-end">
                              <q-btn v-close-popup label="Close" color="primary" flat></q-btn>
                            </div>
                          </q-date>
                        </q-popup-proxy>
                      </i>
                    </template>
                  </q-input>
                </div>

                <div class="hris-form-field">
                  <q-input outlined dense color="green" v-model="start_date" mask="date" label="Start Date">
                    <template v-slot:append>
                      <i class="far fa-calendar-alt cursor-pointer">
                        <q-popup-proxy ref="qDateProxy" transition-show="scale" transition-hide="scale">
                          <q-date v-model="start_date">
                            <div class="row items-center justify-end">
                              <q-btn v-close-popup label="Close" color="primary" flat></q-btn>
                            </div>
                          </q-date>
                        </q-popup-proxy>
                      </i>
                    </template>
                  </q-input>
                </div>

                <div class="hris-form-field">
                  <q-input outlined dense color="green" v-model="end_date" mask="date" label="End Date">
                    <template v-slot:append>
                      <i class="far fa-calendar-alt cursor-pointer">
                        <q-popup-proxy ref="qDateProxy" transition-show="scale" transition-hide="scale">
                          <q-date v-model="end_date">
                            <div class="row items-center justify-end">
                              <q-btn v-close-popup label="Close" color="primary" flat></q-btn>
                            </div>
                          </q-date>
                        </q-popup-proxy>
                      </i>
                    </template>
                  </q-input>
                </div>
              </div>

              <div class="hris-form-row">
                <div class="hris-form-field" style="flex-basis: 100%;">
                  <q-input outlined dense color="green" v-model="monthly_deduct" label="Monthly Deduction" lazy-rules
                    :rules="[val => val && val.length > 0 || 'This field is required']"></q-input>
                </div>
              </div>
            </div>
            <div class="hris-modal-footer">
              <q-btn unelevated class="hris-q-outline" label="Close" v-close-popup></q-btn>
              <q-btn unelevated class="hris-q-primary" label="Submit" type="submit"></q-btn>
            </div>
          </q-form>
        </div>
      </q-dialog>

      <!-- manual code -->
      <q-dialog v-model="loantypeDialog" persistent>
        <div class="hris-modal hris-modal-lg" style="max-width: 920px;">
          <div class="hris-modal-header">
            <h2 class="hris-modal-title">Add Loan Type</h2>
          </div>
          <div class="hris-modal-body">
            <div class="row" style="display: flex; flex-wrap: wrap; gap: 24px;">
              <div style="flex: 1 1 240px; max-width: 280px;">
                <q-form @submit="loanTypeSubmit">
                  <div class="hris-form-field q-mb-md">
                    <q-input outlined dense color="green" v-model="loanType" label="Loan Type" lazy-rules
                      :rules="[val => val && val.length > 0 || 'This field is required']"></q-input>
                  </div>
                  <div class="hris-form-actions" style="justify-content: flex-start; padding: 0;">
                    <q-btn unelevated class="hris-q-outline" label="Close" v-close-popup></q-btn>
                    <q-btn unelevated class="hris-q-primary" label="Submit" type="submit"></q-btn>
                  </div>
                </q-form>
              </div>
              <div style="flex: 1 1 380px;">
                <q-table flat :data="loanTypeData" :columns="loanColumns" row-key="id" :pagination.sync="pagination">
                  <template v-slot:body="props">
                    <q-tr slot="body" :props="props" @click="selectedRow(props)">
                      <q-td key="types" :props="props">{{ props.row.types }}</q-td>
                    </q-tr>
                  </template>
                  <template v-slot:no-data>
                    <div class="hris-empty">No loan types found.</div>
                  </template>
                </q-table>
              </div>
            </div>
          </div>
        </div>
      </q-dialog>

      <!-- import adjustment -->
      <q-dialog v-model="importfile" persistent transition-show="scale" transition-hide="scale">
        <div class="hris-modal">
          <q-form ref="form" autocomplete="off" @submit="uploadFile">
            <div class="hris-modal-header">
              <h2 class="hris-modal-title">Upload Loan</h2>
            </div>
            <div class="hris-modal-body">
              <p class="hris-form-section-hint" style="margin-top: 0;">.csv files only</p>
              <q-file color="green" outlined dense v-model="files" label="Select your file" accept=".csv"
                :rules="[formRules.required]">
                <template v-slot:prepend>
                  <i class="fa fa-upload"></i>
                </template>
              </q-file>
            </div>
            <div class="hris-modal-footer">
              <q-btn unelevated class="hris-q-outline" label="Close" v-close-popup></q-btn>
              <q-btn unelevated class="hris-q-primary" label="Submit" type="submit"></q-btn>
            </div>
          </q-form>
        </div>
      </q-dialog>

      <!-- deduction history -->
      <q-dialog v-model="deductHistory" transition-show="scale" transition-hide="scale">
        <div class="hris-modal">
          <div class="hris-modal-header">
            <div>
              <h2 class="hris-modal-title">Deduction History</h2>
              <p class="hris-modal-subtitle">Total Deduction: <strong style="color: var(--hris-accent);">{{ totaldeductionHistory }}</strong></p>
            </div>
          </div>
          <div class="hris-modal-body">
            <q-list v-if="deductionHistory.length > 0" separator>
              <q-item v-for="(deduct, index) in deductionHistory" :key="index">
                <q-item-section>
                  <q-item-label>{{ deduct.noOfDeduction }} Deduction</q-item-label>
                  <q-item-label caption lines="2">Paydate:
                    <span style="color: var(--hris-accent); font-weight: 600;">{{ deduct.paydate }}</span>
                  </q-item-label>
                  <q-item-label caption lines="2">Cutt Off:
                    <span style="color: var(--hris-accent); font-weight: 600;">{{ deduct.cuttOff }}</span>
                  </q-item-label>
                  <q-item-label caption lines="2">Amount:
                    <span style="color: var(--hris-accent); font-weight: 600;">{{ deduct.amountDeduct }}</span>
                  </q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
            <div v-else class="hris-empty">No Deduction Yet!</div>
          </div>
        </div>
      </q-dialog>
    </div>
  </div>
</template>
<script>
import axios from 'axios';
import mixins from '../../mixins';
import { exportFile } from 'quasar'

function wrapCsvValue(val, formatFn) {
  let formatted = formatFn !== void 0 ? formatFn(val) : val

  formatted = formatted === void 0 || formatted === null ? '' : String(formatted)

  formatted = formatted.split('"').join('""')
  return `"${formatted}"`
}
export default {
  data() {
    return {
      separator: 'cell',
      fromDay: new Date().toISOString().slice(0, 10).replace("-", "/").replace("-", "/"),
      toDay: new Date().toISOString().slice(0, 10).replace("-", "/").replace("-", "/"),
      importfile: false,
      deductHistory: false,
      files: [],
      spinner: false,

      pagination: {
        rowsPerPage: 30
      },

      loantypeDialog: false,
      loanTitle: '',
      manual: false,
      employee_id: '',
      loan_type: '',
      loanTypeOptions: [],
      loan_amt: '',
      actual_amt: '',
      loan_date: new Date().toISOString().slice(0, 10).replace("-", "/").replace("-", "/"),
      start_date: new Date().toISOString().slice(0, 10).replace("-", "/").replace("-", "/"),
      end_date: new Date().toISOString().slice(0, 10).replace("-", "/").replace("-", "/"),
      monthly_deduct: '',
      loan_deduction_id: '',


      generatedData: [],

      searchKey: '',
      data: [],
      columns: [
        { name: 'empID', align: 'center', label: 'Employee ID', field: 'empID', sortable: true },
        { name: 'name', align: 'center', label: 'Name', field: 'name', sortable: true },
        { name: 'loanType', align: 'center', label: 'Loan Type', field: 'loanType', sortable: true },
        { name: 'loanAmount', align: 'center', label: 'Loan Amount', field: 'loanAmount', sortable: true },
        { name: 'actualAmount', align: 'center', label: 'Actual Amount', field: 'actualAmount', sortable: true },
        { name: 'loadDate', align: 'center', label: 'Loan Date', field: 'loadDate', sortable: true },
        { name: 'startDate', align: 'center', label: 'Start Date', field: 'startDate', sortable: true },
        { name: 'endDate', align: 'center', label: 'End Date', field: 'endDate', sortable: true },
        { name: 'monthlyDeduction', align: 'center', label: 'Monthly Deduction', field: 'monthlyDeduction', sortable: true },
        { name: 'loanBalance', align: 'center', label: 'Deduction/Balance', field: 'loanBalance', sortable: true },
        { name: 'status', align: 'center', label: 'Loan Status', field: 'status', sortable: true },
        { name: 'holdOrActive', align: 'center', label: 'Hold/Active', field: 'holdOrActive', sortable: true },
        { align: 'center', label: 'Action', sortable: true },
      ],

      loanColumns: [
        { name: 'types', align: 'center', label: 'Loan Type', field: 'types', sortable: true },
      ],
      loanTypeData: [],
      loanType: '',
      loanTypeTempID: 0,
      deductionHistory: [],
      totaldeductionHistory: 0,
    }
  },

  created() {
    this.getLoanList()
    this.getLoanTypeList()
  },

  methods: {

    openDialog: function () {
      this.loantypeDialog = true
    },

    addManual: function () {
      this.loanTitle = 'ADD LOAN'
      this.manual = true
      this.clearFields();
    },

    editLoan: function (obj) {
      this.loanTitle = 'EDIT LOAN'
      this.manual = true
      this.loan_deduction_id = obj.id
      this.employee_id = obj.empID
      this.loan_type = obj.loanType
      this.loan_amt = obj.loanAmount
      this.actual_amt = obj.actualAmount
      this.loan_date = obj.loadDate
      this.start_date = obj.startDate
      this.end_date = obj.endDate
      this.monthly_deduct = obj.monthlyDeduction
    },

    uploadLoan: function () {
      this.importfile = true
    },

    filterData: function () {
      this.getLoanList()
    },

    selectedRow: function (obj) {
      this.loanTypeTempID = obj.row.id
      this.loanType = obj.row.types
    },

    getLoanList: function () {
      this.spinner = true;
      let formData = new FormData();
      formData.append("fromDay", this.fromDay);
      formData.append("toDay", this.toDay);
      axios.create({
        baseURL: this.apiUrl,
        headers: { 'Authorization': `Bearer ${this.token}` }
      }).post(`/payroll/deduction/list`, formData)
        .then((res) => {
          this.generatedData = res.data.data
          this.spinner = false
          this.dataFlag = true
        });
    },

    getLoanTypeList: function () {
      this.spinner = true
      axios.create({
        baseURL: this.apiUrl,
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      })
        .get(`/payroll/loan/type/list`)
        .then((res) => {
          this.loanTypeOptions = res.data.types
          this.loanTypeData = res.data.data
          this.loanType = ''
          setTimeout(() => {
            this.spinner = false
          }, 500)
        })
    },

    onSubmit: function () {
      this.spinner = true;
      let formData = new FormData();
      formData.append("loanDeductionId", this.loan_deduction_id);
      formData.append("empID", this.employee_id);
      formData.append("loanType", this.loan_type);
      formData.append("loanAmount", this.loan_amt);
      formData.append("actualAmount", this.actual_amt);
      formData.append("loanDate", this.loan_date);
      formData.append("startDate", this.start_date);
      formData.append("endDate", this.end_date);
      formData.append("monthlyDeduct", this.monthly_deduct);

      axios.create({
        baseURL: this.apiUrl,
        headers: { 'Authorization': `Bearer ${this.token}` }
      }).post(`/payroll/deduction`, formData)
        .then((res) => {
          this.spinner = false
          this.getLoanList()
          this.clearFields();
          this.manual = false
        });
    },

    setState: function (status, id) {
      let formData = new FormData();
      formData.append("id", id);
      formData.append("status", status);
      axios.create({
        baseURL: this.apiUrl,
        headers: { 'Authorization': `Bearer ${this.token}` }
      }).post(`/payroll/change-loan-status`, formData)
        .then((res) => {
          this.getLoanList()
        });
    },

    showHistory: function (id) {
      this.deductHistory = true
      let formData = new FormData();
      formData.append("id", id);
      axios.create({
        baseURL: this.apiUrl,
        headers: { 'Authorization': `Bearer ${this.token}` }
      }).post(`/payroll/loan/history`, formData)
        .then((res) => {
          this.totaldeductionHistory = res.data.total
          this.deductionHistory = res.data.data

        });
    },

    loanTypeSubmit: function () {
      let formData = new FormData();
      formData.append("loanType", this.loanType);
      formData.append("loanTypeTempID", this.loanTypeTempID);
      axios.create({
        baseURL: this.apiUrl,
        headers: { 'Authorization': `Bearer ${this.token}` }
      }).post(`/payroll/loan/type`, formData)
        .then((res) => {
          this.getLoanTypeList()
        });
    },

    clearFields: function () {
      this.employee_id = '';
      this.loan_type = '';
      this.loan_amt = '';
      this.actual_amt = '';
      this.loan_date = '';
      this.start_date = '';
      this.end_date = '';
      this.monthly_deduct = '';
    },

    uploadFile: function () {
      this.spinner = true
      let formData = new FormData();
      formData.append("files", this.files);
      axios({
        onUploadProgress: uploadEvent => {
          console.log("Progress: " + Math.round(uploadEvent.loaded / uploadEvent.total * 100) + '%')
        },
        headers: { 'Authorization': `Bearer ${this.token}` },
        url: this.apiUrl + '/payroll/deduction/upload',
        data: formData,
        method: 'POST'
      }).then(response => {
        setTimeout(() => {
          this.spinner = false
          this.importfile = false
          this.files = []
          this.$q.notify({
            message: 'Loan Deduction Uploaded Successfully!',
            color: 'green',
            position: 'center',
            actions: [{ label: 'Dismiss', color: 'white', handler: () => { /* ... */ } }]
          })
        }, 500)
      }).catch((error) => {
        console.log(error)
      })
    },

    getTemplate: function () {
      axios.create({
        baseURL: this.apiUrl,
        headers: {
          'Authorization': `Bearer ${this.token}`
        }
      })
        .get('/employee/template/download')
        .then((res) => {
          this.downloadTemplate(res.data.data.loan_template)
        })
    },

    downloadTemplate: function (url) {
      axios({
        url: this.apiUrl + '/' + url,
        method: 'GET',
        responseType: 'arraybuffer',
      }).then((response) => {
        var fileURL = window.URL.createObjectURL(new Blob([response.data]));
        var fileLink = document.createElement('a');
        fileLink.href = fileURL;
        fileLink.setAttribute('download', 'Loan_Template.csv');
        document.body.appendChild(fileLink);

        fileLink.click();
      });
    },

    exportTable: function (fileExtension) {

      const content = [this.columns.map(col => wrapCsvValue(col.label))].concat(
        this.generatedData.map(row => this.columns.map(col => wrapCsvValue(
          typeof col.field === 'function' ?
            col.field(row) :
            row[col.field === void 0 ? col.name : col.field],
          col.format
        )).join(','))
      ).join('\r\n')

      const status = exportFile(
        `Loan.${fileExtension}`,
        content,
        `text/${fileExtension}`
      );

      if (status !== true) {
        this.$q.notify({
          message: 'Browser denied file download...',
          color: 'negative',
          icon: 'warning'
        })
      }
    },

  }
}
</script>
<style scoped>
</style>
